import { Button, DatePicker, Select, Table, message } from 'antd';
import React, { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  exportOrders,
  getAllProductLines,
  getDistinctYear,
  getOrderFormsByYear,
} from '../../../config/api-routes';
import api from '../../../config/axios';
import { Years } from '../../../schema/order';
import { provinceReport } from '../../../common/province';

const OrderReport = () => {
  const [productTypes, setProductTypes] = useState([]);
  const [ordersList, setOrdersList] = useState([]);

  const [inlineLoading, setInlineLoading] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [orderFormLoading, setOrderFormLoading] = useState(false);
  const [yearLoading, setYearLoading] = useState(false);

  const [selectedProductLine, setSelectedProductLine] = useState<number | null>(0);
  const [distinctYears, setDistinctYears] = useState([]);
  const [selectedDistinctYear, setSelectedDistinctYear] = useState<Years | ''>();
  const [orderFormsDropdown, setOrderFormsDropdown] = useState<any[]>([]);
  const [selectedOrderFormId, setSelectedOrderFormId] = useState(0);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const [selectedValue, setSelectedValue] = useState(null);

  const [dateValue, setDateValue] = useState(null);

  const { RangePicker } = DatePicker;

  useEffect(() => {
    getAllProductTypes();
  }, []);

  const columns = [
    {
      title: 'Order Type',
      dataIndex: 'productType',
    },
    {
      title: 'Order Number',
      dataIndex: 'orderNumber',
    },
    {
      title: 'Name',
      dataIndex: 'name',
    },
    {
      title: 'Business Name',
      dataIndex: 'businessName',
    },
    {
      title: 'Email',
      dataIndex: 'email',
    },
    {
      title: 'Ship Week',
      dataIndex: 'shipWeek',
    },
    {
      title: 'Cancelled',
      dataIndex: 'isCancelled',
    },
  ];
  const toExcelDate = (dateString: string) => {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return null; // invalid date
    return (d.getTime() - new Date(Date.UTC(1899, 11, 30)).getTime()) / (24 * 60 * 60 * 1000);
  };
  const exportToExcel = () => {
    const workbook = XLSX.utils.book_new();

    // Create the "Orders" sheet
    const ordersSheetData = [
      [
        'Order Type',
        'Order Number',
        'Ship Week',
        'Name',
        'Business Name',
        'Email',
        'Province',
        'PL Printed',
        'Packed At',
        'Ship Label',
        'Shipped',
        'Invoiced Date',
        'Cancelled',
        'Heat Pack'
      ],
    ];

    ordersList.forEach((order: any) => {
      ordersSheetData.push([
        order.productType,
        order.orderNumber,
        order.shipWeek,
        order.name,
        order.businessName,
        order.email,
        provinceReport.find((obj) => obj.value === order.province)?.label,
        order.plPrintedAt,
        order.packedAt,
        order.shipLabelPrintedAt,
        order.shippedAt,
        order.invoicedAt,
        order.isCancelled,
        order.allowHeated
      ]);
    });
    const ordersSheet = XLSX.utils.aoa_to_sheet(ordersSheetData);
    ordersSheetData.forEach((row, index) => {
      // Skip the header row (index 0)
      if (index === 0) return;

      const excelRow = index + 1; // Excel rows are 1-based
      const plPrint = row[7];
      const packedAt = row[8]; // column I (index 8, since 0-based)
      const shipLabel = row[9]; // column I (index 8, since 0-based)
      const shippedAt = row[10]; // column I (index 8, since 0-based)
      const invoicedAt = row[11]; // column L (index 11)


      if (plPrint) {
        const jsDate = new Date(plPrint); // directly use ISO string
        if (!isNaN(jsDate.getTime())) {
          ordersSheet[`H${excelRow}`] = {
            t: 'd',
            v: jsDate, // pass Date object
            z: 'yyyy-mm-dd',
          };
        }
      }
      if (packedAt) {
        const jsDate = new Date(packedAt); // directly use ISO string
        if (!isNaN(jsDate.getTime())) {
          ordersSheet[`I${excelRow}`] = {
            t: 'd',
            v: jsDate, // pass Date object
            z: 'yyyy-mm-dd',
          };
        }
      }
      if (shipLabel) {
        const jsDate = new Date(shipLabel); // directly use ISO string
        if (!isNaN(jsDate.getTime())) {
          ordersSheet[`J${excelRow}`] = {
            t: 'd',
            v: jsDate, // pass Date object
            z: 'yyyy-mm-dd',
          };
        }
      }
      if (shippedAt) {
        const jsDate = new Date(shippedAt); // directly use ISO string
        if (!isNaN(jsDate.getTime())) {
          ordersSheet[`K${excelRow}`] = {
            t: 'd',
            v: jsDate, // pass Date object
            z: 'yyyy-mm-dd',
          };
        }
      }
      if (invoicedAt) {
        const jsDate = new Date(invoicedAt);
        if (!isNaN(jsDate.getTime())) {
          ordersSheet[`L${excelRow}`] = {
            t: 'd',
            v: jsDate,
            z: 'yyyy-mm-dd',
          };
        }
      }
    });
    XLSX.utils.book_append_sheet(workbook, ordersSheet, 'Orders');

    // Create the "Products" sheet
    const productsSheetData = [
      [
        'Customer Name',
        'Business Name',
        'Ship Week',
        'Order Number',
        'Email',
        'Variety',
        'Type',
        'Size',
        'Shipment',
        'Ordered Quantity',
        'Available Quantity',
        'Applied Tier',
        'Piece Per Price',
        'Created At',
      ],
    ];

    ordersList.forEach((order: any) => {
      order.products.forEach((product: any) => {
        productsSheetData.push([
          order.name,
          order.businessName,
          order.shipWeek,
          order.orderNumber,
          order.email,
          product.name,
          product.type,
          product.size,
          product?.shipment.toUpperCase(),
          product.orderedQuantity.toString(),
          product.quantity.toString(),
          product.appliedTier,
          product.pricePerPiece,
          product.createdAt,
        ]);
      });
    });
    const productsSheet = XLSX.utils.aoa_to_sheet(productsSheetData);

    productsSheetData.forEach((row, index) => {
      // Skip the header row (index 0)
      if (index === 0) return;

      const excelRow = index + 1; // Excel rows are 1-based

      const orderedQty = Number(row[9]); // Column I (index 7)
      const availableQty = Number(row[10]); // Column J (index 8)
      const pricePerPiece = Number(row[12]); // Column L (index 9)
      const createdAt = row[13]; 
      
      if (!isNaN(orderedQty)) {
        productsSheet[`J${excelRow}`] = { t: 'n', v: orderedQty, z: '0' };
      }

      if (!isNaN(availableQty)) {
        productsSheet[`K${excelRow}`] = { t: 'n', v: availableQty, z: '0' };
      }

      if (!isNaN(pricePerPiece)) {
        productsSheet[`M${excelRow}`] = { t: 'n', v: pricePerPiece, z: '0.00' };
      }

      if (createdAt) {
        const jsDate = new Date(createdAt); // directly use ISO string
        if (!isNaN(jsDate.getTime())) {
          productsSheet[`N${excelRow}`] = {
            t: 'd',
            v: jsDate, // pass Date object
            z: 'yyyy-mm-dd hh:mm',
          };
        }
      }
    });

    XLSX.utils.book_append_sheet(workbook, productsSheet, 'Products');

    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;
    const day = today.getDate();
    const time = today.getHours() + '-' + today.getMinutes() + '-' + today.getSeconds();
    const newdate = day + '-' + month + '-' + year + '_' + time;

    // Save the workbook as an Excel file
    // XLSX.writeFile(workbook, 'OrdersAndProducts.xlsx');
    XLSX.writeFile(workbook, 'OrderData_' + newdate + '.xlsx');
  };

  const getOrders = async () => {
    setOrdersLoading(true);
    await api
      .post(exportOrders, {
        productLineId: selectedProductLine,
        orderFormId: selectedOrderFormId,
        startDate: startDate,
        endDate: endDate,
      })
      .then((result: any) => {
        setOrdersList(result);
        setOrdersLoading(false);
      })
      .catch((error: any) => {
        setOrdersLoading(false);
        message.error(error?.response?.data?.message);
      });
  };

  const getAllProductTypes = async () => {
    setInlineLoading(true);
    await api
      .get(getAllProductLines)
      .then((result: any) => {
        const response = result;
        const productTypesList = response.map((object: any) => ({
          id: object.id,
          value: object.name,
        }));
        setProductTypes(productTypesList);
        setInlineLoading(false);
      })
      .catch((error: any) => {
        message.error(error?.response?.data?.message);
        setInlineLoading(false);
      });
  };

  const handleProductLineChange = async (value: any, event: any) => {
    clearFilters();
    if (event && event.id) {
      const productLineId = event.id;
      setSelectedProductLine(productLineId);
      getDistinctYears(productLineId);

      setSelectedOrderFormId(0);
      setOrderFormsDropdown([]);
    } else {
      setSelectedProductLine(0);
      setSelectedOrderFormId(0);
      setSelectedDistinctYear(undefined);
      setOrderFormsDropdown([]);
      setDistinctYears([]);
      setSelectedValue(null);
      setOrdersList([]);
      setDateValue(null);
      setStartDate('');
      setEndDate('');
    }
  };

  const clearFilters = () => {
    setSelectedDistinctYear(undefined);
    setOrderFormsDropdown([]);
    setSelectedValue(null);
    setSelectedOrderFormId(0);
    setOrdersList([]);
  };

  const onDistinctYearChange = (item: any, event: any) => {
    if (event) {
      const selectedYear = event.value;
      setSelectedDistinctYear(event);
      getOrderForms(selectedProductLine, selectedYear);
    } else {
      setSelectedDistinctYear(undefined);
      setOrderFormsDropdown([]);
      setSelectedValue(null);
      setSelectedOrderFormId(0);
    }
  };

  const orderFormChange = (event: any) => {
    if (event) {
      const selectedOrderFormId = event.key;
      setSelectedOrderFormId(selectedOrderFormId);
      setSelectedValue(event);
    } else {
      setSelectedValue(null);
      setSelectedOrderFormId(0);
    }
  };

  const getDistinctYears = (selectedProductId: any) => {
    setYearLoading(true);
    api
      .get(`${getDistinctYear}/${selectedProductId}`)
      .then((result: any) => {
        const response = result;
        const transformedYearList = response.map((item: any, index: any) => ({
          id: index + 1,
          value: Object.values(item)[0],
        }));

        // If current year found in list , set it selected
        const currentDate = new Date();
        const currentYear = transformedYearList.filter(
          (item: any) => item.value == currentDate.getFullYear(),
        );

        if (currentYear.length) {
          setSelectedDistinctYear(currentYear);
          getOrderForms(selectedProductId, currentYear[0].value);
        }

        setDistinctYears(transformedYearList);
        setYearLoading(false);
      })
      .catch((error: any) => {
        message.error(error?.response?.data?.message);
        setYearLoading(false);
      });
  };

  const getOrderForms = async (productId: any, selectedYear: any) => {
    setOrderFormLoading(true);
    await api
      .get<never, any>(`${getOrderFormsByYear}/${productId}?year=${selectedYear}`)
      .then((res: any) => {
        const orderFormResponse = res;
        const orderFormdd = [];
        for (let i = 0; i < orderFormResponse.length; i++) {
          const object = orderFormResponse[i];
          orderFormdd.push({
            id: object.id,
            value: object.id,
            title: object.title,
            key: object.id,
            label: object.title,
          });
        }
        const orderFormList = orderFormdd.filter((ele) => {
          return ele.title?.toUpperCase() != 'DELETE';
        });

        setOrderFormsDropdown(orderFormList);
        setOrderFormLoading(false);
      })
      .catch((error: any) => {
        console.log(error);
        setOrderFormLoading(false);
      });
  };

  function onCalendarChange(dates: any) {
    setDateValue(dates);
    if (dates != null) {
      dates.map((dateString: string, index: number) => {
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = `0${date.getMonth() + 1}`.slice(-2);
        const day = `0${date.getDate()}`.slice(-2);

        const formattedDate = `${year}-${month}-${day}`;
        if (index == 0) {
          setStartDate(formattedDate);
          setEndDate('');
        } else {
          setEndDate(formattedDate);
        }
      });
    } else {
      setStartDate('');
      setEndDate('');
    }
  }

  const fetchRecords = () => {
    getOrders();
  };

  return (
    <div className='reports-tab-container'>
      <div className='d-flex align-items-center justify-content-center mb-2'>
        <span className='d-flex gap-2'>
          <Select
            placeholder={'Select Product Line'}
            allowClear
            options={productTypes}
            loading={inlineLoading}
            style={{ width: '200px' }}
            onChange={(value, event) => handleProductLineChange(value, event)}
          ></Select>
          <Select
            placeholder={'Select Year'}
            allowClear
            value={selectedDistinctYear}
            options={distinctYears}
            style={{ width: '200px' }}
            loading={yearLoading}
            labelInValue
            onChange={(value, event) => onDistinctYearChange(value, event)}
          ></Select>
          <Select
            placeholder={'Select Order Form'}
            labelInValue
            allowClear
            value={selectedValue}
            loading={orderFormLoading}
            options={orderFormsDropdown}
            onChange={orderFormChange}
            style={{ width: '200px' }}
          ></Select>
          <RangePicker onCalendarChange={onCalendarChange} value={dateValue} />
          <Button
            type='primary'
            disabled={
              (selectedProductLine == 0 && startDate == '' && endDate == '') ||
              ordersLoading ||
              (selectedProductLine != 0 && startDate != '' && endDate == '1970-01-01') ||
              (selectedProductLine != 0 && startDate == '1970-01-01' && endDate != '')
            }
            onClick={fetchRecords}
          >
            Fetch
          </Button>
          <Button
            type='primary'
            disabled={!ordersList.length || ordersLoading}
            onClick={exportToExcel}
          >
            Export
          </Button>
        </span>
      </div>
      <Table
        pagination={{ pageSize: 100, showSizeChanger: false }}
        bordered
        loading={ordersLoading}
        columns={columns}
        dataSource={ordersList}
        rowKey='id'
      />
    </div>
  );
};

export default OrderReport;
